<template>
    <fieldset class="w-full">
        <legend :id="legendId" class="font-bold mb-2">
            {{ field.display }} <span v-if="field.isRequired" class="font-normal text-base text-gray-800">*</span>
        </legend>

        <!-- Error/Help text container -->
        <div v-if="errorMessage || field.instructions" :id="descriptionId" class="mb-2">
            <div v-if="errorMessage" class="text-red-600 text-sm font-semibold" role="alert" aria-live="polite">
                {{ errorMessage }}
            </div>
            <div v-if="field.instructions" class="text-gray-600 text-sm">
                {{ field.instructions }}
            </div>
        </div>

        <div
            role="radiogroup"
            :aria-labelledby="legendId"
            :aria-describedby="(errorMessage || field.instructions) ? descriptionId : undefined"
            :aria-required="field.isRequired"
            :aria-invalid="hasError"
            :class="{'flex flex-wrap gap-4': field.inline}"
        >
            <div
                v-for="(optionLabel, optionValue, index) in fieldOptions"
                :key="optionValue"
                :class="{'block w-full mb-1': !field.inline, 'inline-flex items-center': field.inline}"
            >
                <div
                    :ref="el => setRadioRef(el, index)"
                    role="radio"
                    :aria-checked="isChecked(optionValue) ? 'true' : 'false'"
                    :aria-labelledby="`${field.id}-option-${index}`"
                    :tabindex="getTabIndex(optionValue, index)"
                    :class="[
                        'flex items-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 rounded px-2 py-1 transition-all',
                        hasError ? 'focus:ring-red-500' : 'focus:ring-blue-500'
                    ]"
                    @click="selectOption(optionValue)"
                    @keydown="handleKeyDown($event, optionValue, index)"
                >
                    <span
                        class="inline-flex items-center justify-center w-4 h-4 rounded-full border-2 mr-2 transition-colors"
                        :class="[
                            isChecked(optionValue)
                                ? 'border-blue-600 bg-blue-600'
                                : 'border-gray-400 bg-white',
                            hasError && !isChecked(optionValue) ? 'border-red-500' : ''
                        ]"
                        aria-hidden="true"
                    >
                        <span
                            v-if="isChecked(optionValue)"
                            class="w-2 h-2 rounded-full bg-white"
                        ></span>
                    </span>
                    <span :id="`${field.id}-option-${index}`" class="select-none">
                        {{ optionLabel || optionValue }}
                    </span>
                </div>

                <!-- Hidden input for form submission -->
                <input
                    type="radio"
                    :name="field.handle"
                    :value="optionValue"
                    :checked="isChecked(optionValue)"
                    :required="field.isRequired"
                    class="sr-only"
                    tabindex="-1"
                    aria-hidden="true"
                    @change="selectOption(optionValue)"
                >
            </div>
        </div>
    </fieldset>
</template>

<script setup>
import {defineField} from "./BaseField.js";
import {reactive, ref, computed, onMounted, nextTick} from "vue";

const emit = defineEmits(['change']);

const field = defineField();

const { options, old = null, errors = null } = field

let fieldOptions = options

if(Array.isArray(options)){
    fieldOptions = reactive(options.reduce((acc, cur) => {
        if(cur.key !== undefined && cur.value !== undefined){
            acc[cur.key] = cur.value
        }

        return acc
    }, {}))
}

// Component state
const selectedValue = ref(old);
const focusedIndex = ref(0);
const radioRefs = ref([]);

// Generate unique IDs for accessibility
const legendId = computed(() => `${field.id}-legend`);
const descriptionId = computed(() => `${field.id}-description`);

// Error handling
const errorMessage = computed(() => {
    if (errors && typeof errors === 'object') {
        return Object.values(errors).flat().join(', ');
    }
    return errors || null;
});

const hasError = computed(() => !!errorMessage.value);

const isChecked = (value) => {
    return selectedValue.value === value;
}

const getTabIndex = (value, index) => {
    // If there's a selected value, only it gets tabindex="0"
    if (selectedValue.value !== null) {
        return isChecked(value) ? 0 : -1;
    }
    // Otherwise, first option gets tabindex="0"
    return index === 0 ? 0 : -1;
}

const setRadioRef = (el, index) => {
    if (el) {
        radioRefs.value[index] = el;
    }
}

const selectOption = (value) => {
    selectedValue.value = value;

    // Update focus index
    const optionValues = Object.keys(fieldOptions);
    focusedIndex.value = optionValues.indexOf(value);

    emit('change', value);
}

const focusRadio = (index) => {
    nextTick(() => {
        const radio = radioRefs.value[index];
        if (radio) {
            radio.focus();
        }
    });
}

const handleKeyDown = (event, value, index) => {
    const optionValues = Object.keys(fieldOptions);
    const totalOptions = optionValues.length;
    let newIndex = index;

    switch(event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
            event.preventDefault();
            newIndex = (index + 1) % totalOptions;
            selectOption(optionValues[newIndex]);
            focusRadio(newIndex);
            break;

        case 'ArrowUp':
        case 'ArrowLeft':
            event.preventDefault();
            newIndex = (index - 1 + totalOptions) % totalOptions;
            selectOption(optionValues[newIndex]);
            focusRadio(newIndex);
            break;

        case ' ':
        case 'Enter':
            event.preventDefault();
            selectOption(value);
            break;
    }
}

// Set initial focus index on mount
onMounted(() => {
    if (selectedValue.value !== null) {
        const optionValues = Object.keys(fieldOptions);
        focusedIndex.value = optionValues.indexOf(selectedValue.value);
    }
});
</script>

<style scoped>
/* Screen reader only class for hidden inputs */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}
</style>
